/**
 * screens/settings/connections.tsx — Settings → Connections (CR-MTR-PLT-001 §6).
 * One registry of the org's WhatsApp numbers, phone lines and senders, and the
 * products (and, for an HQ, outlets) bound to each. Only org_admin binds or
 * unbinds (D18); everyone else reads. No prices or balances here.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, Pressable, ActivityIndicator, Platform } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { useSession } from '../../context/sessionContext';
import { useToast } from '../../components/toast';
import { SettingsHeader, Panel, Tag, Divider } from '../../components/platform/primitives';
import { PButton, PModal, PSelect, PTextInput, PCheckbox, PEmpty } from '../../components/platform/controls';
import Alert from '../../components/alert';
import Icon from '../../components/icon';
import { FS, FW } from '../../utils/platformTokens';
import { getApiErrorMessage } from '../../utils/errorHandler';
import { getCurrentUser } from '../../services/auth/auth';
import { subscribedProductsFrom, productDisplayName } from '../../navigation/planGate';
import {
  listConnections, listConnectionProviders, createConnection, bindConnection, unbindConnection,
  connectionHistory, deactivateConnection,
} from '../../services/platform/platformApi';
import type { Capability, Connection, ConnectionProvider, BindingHistoryRow } from '../../schema/platform/types';

export const CAPABILITY_LABEL: Record<Capability, string> = {
  whatsapp: 'WhatsApp',
  telephony: 'Phone lines',
  sms: 'SMS',
  email_smtp: 'Email senders',
  social: 'Social',
};
const CAPABILITY_ORDER: Capability[] = ['whatsapp', 'telephony', 'sms', 'email_smtp', 'social'];

const AddConnectionModal: React.FC<{
  open: boolean;
  onClose: () => void;
  orgId: string;
  products: string[];
  providers: ConnectionProvider[];
  onCreated: () => void;
}> = ({ open, onClose, orgId, products, providers, onCreated }) => {
  const { theme } = useTheme();
  const toast = useToast();
  const capabilities = useMemo(() => CAPABILITY_ORDER.filter((c) => providers.some((p) => p.capability === c)), [providers]);
  const [capability, setCapability] = useState<Capability>('whatsapp');
  const [provider, setProvider] = useState('');
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [err, setErr] = useState<{ name?: string; products?: string; api?: string }>({});
  const [saving, setSaving] = useState(false);
  const forCap = providers.filter((p) => p.capability === capability);

  useEffect(() => {
    if (!open) return;
    const first = capabilities[0] ?? 'whatsapp';
    setCapability(first);
    setProvider(providers.find((p) => p.capability === first)?.provider ?? '');
    setName(''); setPicked([]); setErr({});
  }, [open, capabilities, providers]);

  const submit = async () => {
    const e: typeof err = {};
    if (!name.trim()) e.name = 'Give it a name your team will recognise.';
    if (picked.length === 0) e.products = 'Select at least one product.';
    setErr(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const r = await createConnection(orgId, { capability, provider, displayName: name.trim(), productCodes: picked });
      if (r.onboardingUrl && Platform.OS === 'web') window.open(r.onboardingUrl, '_blank', 'noopener');
      toast.success(`${name.trim()} added.`);
      onCreated();
      onClose();
    } catch (ex) {
      setErr({ api: getApiErrorMessage(ex, "Couldn't add the connection.") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PModal open={open} onClose={onClose} title="Add connection"
      footer={<>
        <PButton kind="ghost" onPress={onClose}>Cancel</PButton>
        <PButton kind="accent" loading={saving} onPress={submit}>Add connection</PButton>
      </>}>
      <View style={{ gap: 14 }}>
        {!!err.api && <Alert tone="danger">{err.api}</Alert>}
        <PSelect label="Type" value={capability}
          onChange={(v) => { setCapability(v as Capability); setProvider(providers.find((p) => p.capability === v)?.provider ?? ''); }}
          options={capabilities.map((c) => ({ value: c, label: CAPABILITY_LABEL[c] }))} />
        <PSelect label="Provider" value={provider} onChange={setProvider}
          options={forCap.map((p) => ({ value: p.provider, label: p.label }))} />
        <PTextInput label="Name" placeholder="Front desk WhatsApp" value={name} onChangeText={setName} error={err.name} />
        <View style={{ gap: 8 }}>
          <Text style={{ fontSize: FS.md, fontWeight: FW.medium, color: theme.colors.ink }}>Products that may use it</Text>
          {products.map((p) => (
            <PCheckbox key={p} label={productDisplayName(p)} checked={picked.includes(p)}
              onChange={(on) => setPicked((cur) => (on ? [...cur, p] : cur.filter((x) => x !== p)))} />
          ))}
          {!!err.products && <Text style={{ color: theme.colors.danger, fontSize: FS.sm }}>{err.products}</Text>}
        </View>
      </View>
    </PModal>
  );
};

const HistoryModal: React.FC<{ connection: Connection | null; onClose: () => void }> = ({ connection, onClose }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const [rows, setRows] = useState<BindingHistoryRow[] | null>(null);
  useEffect(() => {
    if (!connection) return;
    setRows(null);
    connectionHistory(connection.id).then(setRows, () => setRows([]));
  }, [connection]);
  return (
    <PModal open={!!connection} onClose={onClose} title={`History · ${connection?.displayName ?? ''}`} size="wide">
      {rows === null ? <ActivityIndicator color={c.accent} /> : rows.length === 0 ? (
        <Text style={{ color: c.ink3 }}>No binding changes yet.</Text>
      ) : (
        <View style={{ gap: 10 }}>
          {rows.map((r) => (
            <View key={r.id} style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <Tag tone={r.action === 'bound' ? 'success' : 'neutral'}>{r.action === 'bound' ? 'Bound' : 'Unbound'}</Tag>
              <Text style={{ color: c.ink }}>{productDisplayName(r.productCode)} · {r.orgName}</Text>
              <Text style={{ color: c.ink3, fontSize: FS.sm }}>by {r.actorName} · {new Date(r.at).toLocaleString('en-GB')}</Text>
            </View>
          ))}
        </View>
      )}
    </PModal>
  );
};

const ConnectionRow: React.FC<{
  conn: Connection;
  orgId: string;
  canEdit: boolean;
  products: string[];
  onChanged: () => void;
  onHistory: () => void;
}> = ({ conn, orgId, canEdit, products, onChanged, onHistory }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const toast = useToast();
  const own = conn.ownerOrgId === orgId;
  const productBindings = conn.bindings.filter((b) => b.orgId === conn.ownerOrgId);
  const outletBindings = conn.bindings.filter((b) => b.orgId !== conn.ownerOrgId);
  const unbound = products.filter((p) => !productBindings.some((b) => b.productCode === p));
  const [adding, setAdding] = useState('');
  const editable = canEdit && own && conn.status === 'active';

  const run = async (fn: () => Promise<void>, ok: string) => {
    try { await fn(); toast.success(ok); onChanged(); } catch (e) { toast.error(getApiErrorMessage(e, 'That change was refused.')); }
  };

  return (
    <View style={{ paddingVertical: 12, gap: 8 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <Text style={{ fontWeight: FW.semibold, color: c.ink, fontSize: FS.md }}>{conn.displayName}</Text>
        <Text style={{ color: c.ink3, fontSize: FS.sm }}>{conn.provider}</Text>
        {conn.status === 'deactivated' && <Tag tone="neutral">Deactivated</Tag>}
        {!own && <Tag tone="info">Shared by {conn.ownerOrgName}</Tag>}
        <View style={{ flex: 1 }} />
        <PButton size="sm" kind="ghost" icon="history" onPress={onHistory}>History</PButton>
        {editable && (
          <PButton size="sm" kind="ghost" textColor={c.danger}
            onPress={() => run(() => deactivateConnection(conn.id), `${conn.displayName} deactivated.`)}>Deactivate</PButton>
        )}
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <Text style={{ color: c.ink3, fontSize: FS.sm, marginRight: 4 }}>Products</Text>
        {productBindings.length === 0 && <Text style={{ color: c.ink3, fontSize: FS.sm }}>None</Text>}
        {productBindings.map((b) => (
          <View key={b.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
            <Tag tone="accent">{productDisplayName(b.productCode)}</Tag>
            {editable && (
              <Pressable accessibilityLabel={`Unbind ${productDisplayName(b.productCode)}`} hitSlop={10}
                style={{ minWidth: 28, minHeight: 28, alignItems: 'center', justifyContent: 'center' }}
                onPress={() => run(() => unbindConnection(conn.id, b.id), `${productDisplayName(b.productCode)} unbound.`)}>
                <Icon name="x" size={12} color={c.ink3} />
              </Pressable>
            )}
          </View>
        ))}
        {editable && unbound.length > 0 && (
          <View style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}>
            <PSelect size="sm" width={170} value={adding} onChange={setAdding}
              options={[{ value: '', label: 'Bind a product…' }, ...unbound.map((p) => ({ value: p, label: productDisplayName(p) }))]} />
            <PButton size="sm" disabled={!adding}
              onPress={() => run(() => bindConnection(conn.id, orgId, adding), `${productDisplayName(adding)} bound.`).then(() => setAdding(''))}>Bind</PButton>
          </View>
        )}
      </View>
      {outletBindings.length > 0 && (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <Text style={{ color: c.ink3, fontSize: FS.sm, marginRight: 4 }}>Outlets</Text>
          {outletBindings.map((b) => <Tag key={b.id} tone="neutral">{b.orgName} · {productDisplayName(b.productCode)}</Tag>)}
        </View>
      )}
    </View>
  );
};

const ConnectionsScreen: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { org, role } = useSession();
  const orgId = org?.id ?? '';
  const canEdit = role === 'org_admin';
  const [conns, setConns] = useState<Connection[] | null>(null);
  const [providers, setProviders] = useState<ConnectionProvider[]>([]);
  const [products, setProducts] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [history, setHistory] = useState<Connection | null>(null);

  const load = useCallback(() => {
    if (!orgId) return;
    listConnections(orgId).then(
      (rows) => { setConns(rows); setError(null); },
      (e) => { setConns([]); setError(getApiErrorMessage(e, "Couldn't load connections.")); },
    );
  }, [orgId]);

  useEffect(() => {
    load();
    listConnectionProviders().then(setProviders, () => setProviders([]));
    getCurrentUser().then((me) => setProducts(subscribedProductsFrom(me) ?? []), () => setProducts([]));
  }, [load]);

  const groups = CAPABILITY_ORDER
    .map((cap) => ({ cap, rows: (conns ?? []).filter((x) => x.capability === cap) }))
    .filter((g) => g.rows.length > 0);

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1000, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader
        title="Connections"
        sub="WhatsApp numbers, phone lines and email senders, and which products may use each."
        action={canEdit && <PButton kind="accent" icon="plus" disabled={providers.length === 0} onPress={() => setAdding(true)}>Add connection</PButton>}
      />
      {!canEdit && <View style={{ marginBottom: 16 }}><Alert tone="info">Only an organisation owner can add, bind or unbind connections.</Alert></View>}
      {error && <View style={{ marginBottom: 16 }}><Alert tone="danger">{error}</Alert></View>}

      {conns === null ? <ActivityIndicator color={c.accent} /> : groups.length === 0 ? (
        <PEmpty icon="link" title="No connections yet">Add a WhatsApp number or phone line, then choose which products may use it.</PEmpty>
      ) : groups.map((g) => (
        <Panel key={g.cap} title={CAPABILITY_LABEL[g.cap]}>
          {g.rows.map((conn, i) => (
            <View key={conn.id}>
              {i > 0 && <Divider style={{ marginVertical: 0 }} />}
              <ConnectionRow conn={conn} orgId={orgId} canEdit={canEdit} products={products} onChanged={load} onHistory={() => setHistory(conn)} />
            </View>
          ))}
        </Panel>
      ))}

      <AddConnectionModal open={adding} onClose={() => setAdding(false)} orgId={orgId} products={products} providers={providers} onCreated={load} />
      <HistoryModal connection={history} onClose={() => setHistory(null)} />
    </ScrollView>
  );
};

export default ConnectionsScreen;
